import { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Lightbulb, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Restaurant",
  "Cafe",
  "Bar",
  "Retail",
  "Salon/Barbershop",
  "Grocery",
  "Convenience Store",
  "Pharmacy",
  "Gym",
  "Arts Venue",
  "Public Services",
  "Bank",
  "Other"
];

export const SuggestBusinessDialog = ({ 
  open, 
  onOpenChange, 
  neighborhoods 
}) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    neighborhood: '',
    category: '',
    suggested_by_email: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter the business name');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter the address');
      return;
    }
    if (!formData.neighborhood) {
      toast.error('Please select a neighborhood');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.suggested_by_email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/suggestions`, formData);
      setSubmitted(true);
      toast.success('Thank you for your suggestion!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit suggestion');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        address: '',
        neighborhood: '',
        category: '',
        suggested_by_email: '',
        notes: ''
      });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="suggest-business-dialog">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-display font-semibold mb-2">Thank You!</h3>
            <p className="text-muted-foreground mb-6">
              Your suggestion has been submitted. Our team will review it and add it to the map if approved.
            </p>
            <Button onClick={handleClose} className="rounded-full">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-display">
                <Lightbulb className="w-5 h-5 text-secondary" />
                Suggest a Business
              </DialogTitle>
              <DialogDescription>
                Know a business that should be on our map? Let us know and we'll add it for the community to review.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Joe's Coffee Shop"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  data-testid="suggest-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  placeholder="e.g., 123 Bedford Ave, Brooklyn"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  data-testid="suggest-address-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Neighborhood *</Label>
                  <Select
                    value={formData.neighborhood}
                    onValueChange={(value) => handleChange('neighborhood', value)}
                  >
                    <SelectTrigger data-testid="suggest-neighborhood-select">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="z-[99999]">
                      {neighborhoods.map((hood) => (
                        <SelectItem key={hood} value={hood}>
                          {hood}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleChange('category', value)}
                  >
                    <SelectTrigger data-testid="suggest-category-select">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="z-[99999]">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Your Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.suggested_by_email}
                  onChange={(e) => handleChange('suggested_by_email', e.target.value)}
                  data-testid="suggest-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  We'll notify you when the business is added
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional details about this business..."
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={2}
                  className="resize-none"
                  data-testid="suggest-notes-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-full bg-secondary hover:bg-secondary/90"
                  disabled={loading}
                  data-testid="submit-suggestion-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Suggestion'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
